<template>
  <textarea
    v-if="!item.is_field"
    ref="contentRef"
    v-model="templateStore.layers[layerIndex].items[itemIndex].value"
    class="BaseTemplateContentText transition"
    :style="dynamicStyles"
    v-bind="$attrs"
    @click="templateStore.handleActiveItem(item)"
  />
  <!-- A data field stays on one line; when it doesn't fit, the tooltip shows the full field name -->
  <div
    v-else
    ref="contentRef"
    class="BaseTemplateContentText BaseTemplateContentText--field transition"
    :style="dynamicStyles"
    v-bind="$attrs"
    @click="templateStore.handleActiveItem(item)"
  >
    {{ fieldText }}
    <k-tooltip
      v-if="!hasPrinter && item.value?.title && !templateStore.isDragging"
      :label="item.value.title"
      position="top"
    >
      <span class="row no-wrap items-center gap-xs text-static-light">
        <k-icon name="link-2" color="info-70" size="16px" />
        {{ item.value.title }}
      </span>
    </k-tooltip>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useTemplateStore } from 'src/stores'

defineOptions({ name: 'BaseTemplateContentText', inheritAttrs: false })
const props = defineProps({
  item: { type: Object, required: true },
  layerIndex: { type: Number, required: true },
  itemIndex: { type: Number, required: true },
  hasPrinter: { type: Boolean, default: false },
})

// Same as HEIGHT_PADDING in BaseTemplateItemTextSetting: the single-line height for a font size
const HEIGHT_PADDING = 8

const templateStore = useTemplateStore()
const contentRef = ref(null)

const isSelected = computed(() => templateStore.selectedItemIds.includes(props.item.id))

const fieldText = computed(() =>
  props.item.value && !props.hasPrinter
    ? `[${props.item.value.title}]`
    : props.item.value && props.hasPrinter
      ? props.item.value.title
      : '',
)

const dynamicStyles = computed(
  () =>
    `width: ${props.item.width}px; height: ${props.item.height}px; font-size: ${props.item.font_size}px; font-weight: ${props.item.font_weight}; text-align: ${props.item.text_align}; color: ${props.item.colour}; font-family: ${props.item.font_family}`,
)

// Text items have no height control, so the height follows the wrapped lines. The selected text (just added,
// clicked or being edited) fits its lines exactly; the others are only touched when they wrap, so opening a
// template doesn't change heights that were saved. A data field never wraps.
function fitHeightToContent() {
  const element = contentRef.value
  if (!element || !element.getClientRects().length) return

  // The `transition` class animates height, so collapsing with it on keeps reporting the old height
  element.style.transition = 'none'
  element.style.height = '0px'
  const contentHeight = element.scrollHeight
  element.style.height = `${props.item.height}px`
  // Apply the restored height before transitions return, so it does not animate up from 0
  element.getBoundingClientRect()
  element.style.transition = ''

  // Read the rendered line height: the global `* { line-height: 150% !important }` decides it, not this component.
  // Counting whole lines also absorbs the rounding the app `zoom` adds to scrollHeight.
  const lineHeight = parseFloat(getComputedStyle(element).lineHeight)
  const lineCount = Math.max(1, Math.round(contentHeight / lineHeight))

  let height
  if (lineCount > 1 || isSelected.value) height = Math.ceil(lineCount * lineHeight)
  // Not selected and back to a single line after being wrapped; other single-line heights are left as saved
  else if (props.item.height >= lineHeight * 2) height = props.item.font_size + HEIGHT_PADDING
  if (height && height !== props.item.height) {
    templateStore.layers[props.layerIndex].items[props.itemIndex].height = height
  }
}

// Re-measure once a newly used font has loaded, since it changes where lines wrap
function scheduleFit() {
  fitHeightToContent()
  document.fonts?.ready.then(fitHeightToContent)
}

watch(
  () => [
    props.item.is_field ? fieldText.value : props.item.value,
    props.item.width,
    props.item.height,
    props.item.font_size,
    props.item.font_weight,
    props.item.font_family,
    props.item.is_show,
    isSelected.value,
  ],
  scheduleFit,
  { flush: 'post' },
)

onMounted(scheduleFit)
</script>

<style lang="sass" scoped>
@import "src/shared/styles/mixins.sass"

.BaseTemplateContentText
  display: block
  padding: 0 2px
  resize: none
  overflow: hidden
  @include state-modifier('field')
    white-space: nowrap
    text-overflow: ellipsis
</style>
